import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DealCard } from '@/components/pipeline/deal-card';
import { cn, formatCurrency } from '@/lib/utils';
import { useSettingsStore } from '@/stores/settings.store';
import type { Deal, PipelineStage } from '@trinco/shared';

interface KanbanColumnProps {
  stage: PipelineStage;
  deals: Deal[];
}

export function KanbanColumn({ stage, deals }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.slug,
  });

  const baseCurrency = useSettingsStore((s) => s.baseCurrency);
  const totalValue = deals.reduce((sum, d) => sum + (d.amountCad ?? d.amount ?? 0), 0);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex w-72 shrink-0 flex-col rounded-lg border bg-muted/30 transition-colors',
        isOver && 'border-primary/50 bg-primary/5',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <div
            className="h-3 w-3 rounded-full"
            style={{ backgroundColor: stage.color ?? '#6B7280' }}
          />
          <h3 className="text-sm font-semibold">{stage.name}</h3>
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium">
            {deals.length}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">
          {formatCurrency(totalValue, baseCurrency)}
        </span>
      </div>

      {/* Cards */}
      <SortableContext items={deals.map((d) => d.id)} strategy={verticalListSortingStrategy}>
        <div className="flex-1 space-y-2 overflow-y-auto p-2">
          {deals.length === 0 ? (
            <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
              Drop deals here
            </div>
          ) : (
            deals.map((deal) => <DealCard key={deal.id} deal={deal} />)
          )}
        </div>
      </SortableContext>
    </div>
  );
}
